'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';
import { isNative } from '@/lib/native';
import { isProductAnalyticsHost } from './analytics-host';

/**
 * Shared product tags are scoped to SweetSpot production hosts, never custom
 * storefronts or native.
 *
 * Page views: the `config` command sends the landing page_view, and the GA4
 * stream's Enhanced Measurement ("page changes based on browser history
 * events", on for this property) sends one per client-side navigation. No
 * manual page_view is emitted here — an earlier `send_page_view: false` plus a
 * second `config` per route sent nothing, so landing views were missing and
 * 46% of sessions had landing page "(not set)".
 */
/**
 * Guest order and reservation pages carry their bearer token in the path. The
 * landing page_view must not send it to Google; flows that land there navigate
 * with a full page load so this config call is the page_view that runs.
 */
export const SECRET_PATH_SEGMENT = '/(\\/(?:order|reservation)\\/)[^/?#]+/';

export function AnalyticsScripts({
  gaMeasurementId,
  gtmId,
}: {
  gaMeasurementId?: string;
  gtmId?: string;
}) {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    setEnabled(!isNative() && isProductAnalyticsHost(window.location.protocol, window.location.hostname));
  }, []);
  if (!enabled) return null;

  const directGa = Boolean(gaMeasurementId) && !gtmId;
  return (
    <>
      {directGa && gaMeasurementId && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`}
            strategy="afterInteractive"
          />
          <Script id="ga4" strategy="afterInteractive">
            {`window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
window.gtag = gtag;
gtag('js', new Date());
gtag('config', '${gaMeasurementId}', { page_location: location.href.replace(${SECRET_PATH_SEGMENT}, '$1:token') });`}
          </Script>
        </>
      )}
      {gtmId && (
        <>
          <Script id="gtm" strategy="afterInteractive">
            {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`}
          </Script>
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
              title="Google Tag Manager"
            />
          </noscript>
        </>
      )}
    </>
  );
}
